import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaCalendarAlt, FaCheckCircle, FaClipboardList, FaCommentAlt,
  FaEdit, FaHeading, FaPlus, FaSave,
  FaTimes, FaTrash, FaUser, FaUserCheck,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiUrl, getUsersEmail } from '../authConfig';
import { getValidToken } from '../utils/msalTokenUtil';
import TruncatedComment from '../components/Common/TruncatedComment';
import { getAssigneeDisplayName } from '../utils/assigneeMapping';
import ActionItemStatusDropdown from '../components/ActionItems/ActionItemStatusDropdown';
import './ActionItemsDrawer.css';

const statusOptions = ['Open', 'In Progress', 'Completed'];
const assignedToOptions = getUsersEmail();

const getQuickDate = (daysAhead) => {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDueDateBadge = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date().toISOString().split('T')[0];
  if (dueDate < today) {
    return <span className="badge rounded-pill" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.68rem', border: '1px solid #fca5a5' }}>Past due</span>;
  }
  if (dueDate === today) {
    return <span className="badge rounded-pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.68rem', border: '1px solid #fde68a' }}>Due today</span>;
  }
  return <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#166534', fontSize: '0.68rem', border: '1px solid #86efac' }}>Upcoming</span>;
};

const ActionItemsDrawer = ({
  isOpen,
  onClose,
  customer,
  customerType,
  account,
  msalInstance,
  isDialerOpen,
  initialEditItem = null,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    title: '',
    dueDate: '',
    comments: '',
    assignedTo: '',
    status: 'Open',
  });
  const [errors, setErrors] = useState({});

  const formRef = useRef(null);
  const requestSequence = useRef(0);

  const customerId = customer?._id || customer?.id;
  const customerName = customerType === 'Corporate' ? customer?.businessName : customer?.name;

  const userEmail = (account?.username || account?.name || '').trim().toLowerCase();

  const loadItems = useCallback(async () => {
    if (!customerId) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const query = new URLSearchParams({ customerType, customerId });
      const response = await fetch(getApiUrl(`/v1/action-items?${query}`), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Unable to load action items.');
      const nextItems = await response.json();
      if (sequence === requestSequence.current) setItems(nextItems);
    } catch (error) {
      console.error(error);
      if (sequence === requestSequence.current) toast.error(error.message || 'Could not load action items.');
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [account, customerId, customerType, msalInstance]);

  useEffect(() => {
    if (!isOpen) {
      requestSequence.current += 1;
      setEditingItem(null);
      setErrors({});
      return undefined;
    }
    setItems([]);
    setErrors({});
    if (initialEditItem) {
      setEditingItem(initialEditItem);
      setForm({
        title: initialEditItem.title || '',
        dueDate: initialEditItem.due_date || '',
        comments: initialEditItem.comments || '',
        assignedTo: initialEditItem.assigned_to || '',
        status: initialEditItem.status || 'Open',
      });
    } else {
      setEditingItem(null);
      // Automatically populate assignedTo with the currently logged-in user's email address
      setForm({
        title: '',
        dueDate: '',
        comments: '',
        assignedTo: userEmail,
        status: 'Open',
      });
    }
    loadItems();
    return () => { requestSequence.current += 1; };
  }, [isOpen, initialEditItem, loadItems, userEmail]);

  const handleStartEdit = (item) => {
    setEditingItem(item);
    setErrors({});
    setForm({
      title: item.title || '',
      dueDate: item.due_date || '',
      comments: item.comments || '',
      assignedTo: item.assigned_to || '',
      status: item.status || 'Open',
    });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setErrors({});
    setForm({
      title: '',
      dueDate: '',
      comments: '',
      assignedTo: userEmail,
      status: 'Open',
    });
  };

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAssignToMyself = () => {
    handleFieldChange('assignedTo', userEmail);
    toast.info(`Assigned to yourself (${getAssigneeDisplayName(userEmail)})`);
  };

  const validateForm = () => {
    const errs = {};
    if (!form.title || !form.title.trim()) {
      errs.title = 'Task Title is mandatory.';
    }
    if (!form.dueDate || !form.dueDate.trim()) {
      errs.dueDate = 'Due Date is mandatory.';
    }
    if (!form.assignedTo || !form.assignedTo.trim()) {
      errs.assignedTo = 'Please select an assignee or click "Assign to Myself".';
    }
    if (!form.comments || !form.comments.trim()) {
      errs.comments = 'Description / Notes are mandatory.';
    }
    if (!form.status || !form.status.trim()) {
      errs.status = 'Status is mandatory.';
    }
    return errs;
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please complete all mandatory fields marked in red.');
      const firstField = Object.keys(validationErrors)[0];
      const elementId = firstField === 'dueDate' ? 'action-item-due-date'
        : firstField === 'assignedTo' ? 'action-item-assigned-to'
        : firstField === 'title' ? 'action-item-title'
        : firstField === 'comments' ? 'action-item-comments'
        : 'action-item-status';
      const el = document.getElementById(elementId);
      if (el) el.focus();
      return;
    }

    setSaving(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const isEditing = Boolean(editingItem);
      const url = isEditing
        ? getApiUrl(`/v1/action-items/${editingItem.id}`)
        : getApiUrl('/v1/action-items');
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...form,
          title: form.title.trim(),
          comments: form.comments.trim(),
          assignedTo: form.assignedTo.trim(),
          customerType,
          customerId,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Unable to ${isEditing ? 'update' : 'save'} action item.`);
      }
      setForm({
        title: '',
        dueDate: '',
        comments: '',
        assignedTo: userEmail,
        status: 'Open',
      });
      setErrors({});
      setEditingItem(null);
      await loadItems();
      toast.success(isEditing ? 'Action item updated successfully.' : 'Action item created successfully.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || `Could not ${editingItem ? 'update' : 'save'} action item.`);
    } finally {
      setSaving(false);
    }
  };

  const handleRowStatusChange = (updatedItem, newStatus) => {
    if (updatedItem?.id) {
      setItems(prev => prev.map(it => it.id === updatedItem.id ? { ...it, ...updatedItem, status: newStatus } : it));
    }
    loadItems();
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this action item?')) return;
    setDeletingId(itemId);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const response = await fetch(getApiUrl(`/v1/action-items/${itemId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Unable to delete action item.');
      }
      setItems(previous => previous.filter(item => item.id !== itemId));
      if (editingItem?.id === itemId) {
        handleCancelEdit();
      }
      toast.success('Action item deleted.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Could not delete action item.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {
    handleCancelEdit();
    onClose();
  };

  return (
    <>
      <div className={`action-items-backdrop${isOpen ? ' show' : ''}`} onClick={handleClose} />
      <aside
        className={`action-items-drawer${isOpen ? ' show' : ''}${isOpen && isDialerOpen ? ' shifted-for-dialer' : ''}`}
        aria-label={`Action items for ${customerName || 'customer'}`}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <header className="action-items-header">
          <div className="d-flex align-items-center gap-3">
            <span className="action-items-icon"><FaClipboardList /></span>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h2 className="h5 fw-bold mb-0 text-dark">Action Items</h2>
                {customerType && (
                  <span
                    className="badge rounded-pill fw-semibold"
                    style={{
                      backgroundColor: customerType === 'Corporate' ? '#f5f3ff' : '#eff6ff',
                      color: customerType === 'Corporate' ? '#6d28d9' : '#1d4ed8',
                      border: `1px solid ${customerType === 'Corporate' ? '#ddd6fe' : '#bfdbfe'}`,
                      fontSize: '0.7rem',
                      padding: '3px 8px',
                    }}
                  >
                    {customerType}
                  </span>
                )}
              </div>
              <div className="small text-muted text-truncate mt-1" style={{ maxWidth: '420px' }}>
                {customerName || 'Customer Account'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="action-items-close-btn"
            onClick={handleClose}
            aria-label="Close action items drawer"
            title="Close drawer"
          >
            <FaTimes size={15} />
          </button>
        </header>

        {/* Content Body */}
        <div className="action-items-content">
          {/* Section 1: Existing Items */}
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h3 className="h6 fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                Existing Action Items
                <span className="badge rounded-pill bg-light text-secondary border fw-normal" style={{ fontSize: '0.75rem' }}>
                  {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
              </h3>
            </div>

            {loading ? (
              <div className="text-center py-4 text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading action items...
              </div>
            ) : items.length === 0 ? (
              <div className="empty-action-items-box text-center p-4 rounded-4">
                <div className="empty-icon-circle mx-auto mb-2">
                  <FaClipboardList size={20} className="text-primary opacity-75" />
                </div>
                <h4 className="h6 fw-bold mb-1 text-dark">No action items yet</h4>
                <p className="text-muted small mb-0" style={{ maxWidth: '360px', margin: '0 auto' }}>
                  Create an action item below to track deadlines, assign team members, and document follow-ups.
                </p>
              </div>
            ) : (
              <div className="table-responsive rounded-3 border bg-white shadow-sm overflow-hidden">
                <table className="table table-hover align-middle mb-0 action-items-table">
                  <thead className="table-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3 py-2">Due Date</th>
                      <th className="py-2">Task Details</th>
                      <th className="py-2">Assignee</th>
                      <th className="py-2">Status</th>
                      <th aria-label="Actions" className="text-end pe-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => {
                      const isRowEditing = editingItem?.id === item.id;
                      const itemDate = new Date(`${item.due_date}T00:00:00`);
                      return (
                        <tr key={item.id} className={isRowEditing ? 'action-item-editing-row' : ''}>
                          <td className="ps-3 text-nowrap">
                            <div className="fw-semibold text-dark">{itemDate.toLocaleDateString()}</div>
                            <div className="mt-1">{getDueDateBadge(item.due_date)}</div>
                          </td>
                          <td style={{ minWidth: 160, maxWidth: 260 }}>
                            {item.title && (
                              <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.85rem' }} title={item.title}>
                                {item.title}
                              </div>
                            )}
                            <div className="text-secondary small mt-1">
                              <TruncatedComment text={item.comments} wordLimit={6} />
                            </div>
                          </td>
                          <td className="text-nowrap">
                            <span className="small text-dark fw-medium">
                              {getAssigneeDisplayName(item.assigned_to)}
                            </span>
                          </td>
                          <td>
                            <ActionItemStatusDropdown
                              item={item}
                              onStatusChange={handleRowStatusChange}
                              account={account}
                              msalInstance={msalInstance}
                              disabled={deletingId === item.id}
                            />
                          </td>
                          <td className="text-end pe-3">
                            <div className="action-items-actions justify-content-end">
                              <button
                                type="button"
                                className={`btn btn-sm btn-action-icon ${isRowEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleStartEdit(item)}
                                disabled={deletingId === item.id}
                                aria-label={`Edit action item due ${item.due_date}`}
                                title={isRowEditing ? 'Currently editing' : 'Edit action item'}
                              >
                                <FaEdit size={12} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-action-icon btn-outline-danger"
                                onClick={() => handleDelete(item.id)}
                                disabled={deletingId === item.id}
                                aria-label={`Delete action item due ${item.due_date}`}
                                title="Delete action item"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Section 2: Form */}
          <section ref={formRef} className="action-item-form-card rounded-4 p-4 border mb-2">
            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <div className={`form-section-icon rounded-3 ${editingItem ? 'bg-warning-subtle text-warning-emphasis' : 'bg-primary-subtle text-primary'}`}>
                  {editingItem ? <FaEdit size={14} /> : <FaPlus size={14} />}
                </div>
                <div>
                  <h3 className="h6 fw-bold mb-0 text-dark">
                    {editingItem ? `Edit Action Item #${editingItem.id}` : 'Create New Action Item'}
                  </h3>
                  <small className="text-muted" style={{ fontSize: '0.74rem' }}>
                    {editingItem ? 'Update details below and click Save Changes' : 'Fill in all mandatory fields (*) to set a deadline, assignee, and notes'}
                  </small>
                </div>
              </div>
              {editingItem && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1 px-2 d-flex align-items-center gap-1"
                  onClick={handleCancelEdit}
                >
                  <FaTimes size={11} /> Cancel
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-3">
                {/* Task Title (Mandatory) */}
                <div className="col-12">
                  <label className="form-label form-label-custom d-flex align-items-center gap-1" htmlFor="action-item-title">
                    <FaHeading className="text-primary opacity-75" size={12} /> Task Title <span className="text-danger">*</span>
                  </label>
                  <input
                    id="action-item-title"
                    type="text"
                    className={`form-control form-control-custom ${errors.title ? 'is-invalid' : ''}`}
                    placeholder="e.g. Upload missing receipts, File corporate T2 return, Client follow-up"
                    value={form.title}
                    onChange={e => handleFieldChange('title', e.target.value)}
                  />
                  {errors.title && (
                    <div className="invalid-feedback d-block mt-1 font-size-xs">{errors.title}</div>
                  )}
                </div>

                {/* Due Date (Mandatory) */}
                <div className="col-12 col-md-6">
                  <label className="form-label form-label-custom d-flex align-items-center gap-1" htmlFor="action-item-due-date">
                    <FaCalendarAlt className="text-primary opacity-75" size={12} /> Due Date <span className="text-danger">*</span>
                  </label>
                  <input
                    id="action-item-due-date"
                    type="date"
                    className={`form-control form-control-custom ${errors.dueDate ? 'is-invalid' : ''}`}
                    value={form.dueDate}
                    onChange={e => handleFieldChange('dueDate', e.target.value)}
                  />
                  {errors.dueDate && (
                    <div className="invalid-feedback d-block mt-1 font-size-xs">{errors.dueDate}</div>
                  )}
                  {/* Quick Date Chips */}
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    <button
                      type="button"
                      className={`btn btn-xs quick-date-chip ${form.dueDate === getQuickDate(0) ? 'active' : ''}`}
                      onClick={() => handleFieldChange('dueDate', getQuickDate(0))}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs quick-date-chip ${form.dueDate === getQuickDate(1) ? 'active' : ''}`}
                      onClick={() => handleFieldChange('dueDate', getQuickDate(1))}
                    >
                      Tomorrow
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs quick-date-chip ${form.dueDate === getQuickDate(3) ? 'active' : ''}`}
                      onClick={() => handleFieldChange('dueDate', getQuickDate(3))}
                    >
                      +3 Days
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs quick-date-chip ${form.dueDate === getQuickDate(7) ? 'active' : ''}`}
                      onClick={() => handleFieldChange('dueDate', getQuickDate(7))}
                    >
                      +1 Week
                    </button>
                  </div>
                </div>

                {/* Status (Mandatory) */}
                <div className="col-12 col-md-6">
                  <label className="form-label form-label-custom d-flex align-items-center gap-1" htmlFor="action-item-status">
                    <FaCheckCircle className="text-primary opacity-75" size={12} /> Status <span className="text-danger">*</span>
                  </label>
                  <select
                    id="action-item-status"
                    className={`form-select form-control-custom ${errors.status ? 'is-invalid' : ''}`}
                    value={form.status}
                    onChange={e => handleFieldChange('status', e.target.value)}
                  >
                    {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                  {errors.status && (
                    <div className="invalid-feedback d-block mt-1 font-size-xs">{errors.status}</div>
                  )}

                </div>

                {/* Assigned To (Mandatory) with "Assign to Myself" Shortcut */}
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label form-label-custom d-flex align-items-center gap-1 mb-0" htmlFor="action-item-assigned-to">
                      <FaUser className="text-primary opacity-75" size={12} /> Assigned To <span className="text-danger">*</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary py-0 px-2 d-inline-flex align-items-center gap-1 rounded-pill assign-myself-shortcut-btn"
                      onClick={handleAssignToMyself}
                      title="Assign task to yourself"
                    >
                      <FaUserCheck size={11} /> Assign to Myself
                    </button>
                  </div>

                  <select
                    id="action-item-assigned-to"
                    className={`form-select form-control-custom ${errors.assignedTo ? 'is-invalid' : ''}`}
                    value={form.assignedTo}
                    onChange={e => handleFieldChange('assignedTo', e.target.value)}
                  >
                    <option value="">-- Select Team Member --</option>
                    {userEmail && (
                      <option value={userEmail}>
                        ⭐ Assign to Myself ({getAssigneeDisplayName(userEmail)})
                      </option>
                    )}
                    {assignedToOptions.map(email => (
                      <option key={email} value={email}>
                        {getAssigneeDisplayName(email)}
                      </option>
                    ))}
                  </select>
                  {errors.assignedTo && (
                    <div className="invalid-feedback d-block mt-1 font-size-xs">{errors.assignedTo}</div>
                  )}

                </div>

                {/* Comments / Description (Mandatory) */}
                <div className="col-12">
                  <label className="form-label form-label-custom d-flex align-items-center gap-1" htmlFor="action-item-comments">
                    <FaCommentAlt className="text-primary opacity-75" size={12} /> Description / Comments / Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="action-item-comments"
                    className={`form-control form-control-custom ${errors.comments ? 'is-invalid' : ''}`}
                    rows="3"
                    placeholder="Enter instructions, notes, action points, or required follow-ups for this customer..."
                    value={form.comments}
                    onChange={e => handleFieldChange('comments', e.target.value)}
                  />
                  {errors.comments && (
                    <div className="invalid-feedback d-block mt-1 font-size-xs">{errors.comments}</div>
                  )}
                </div>
              </div>

              {/* Form In-Card Actions (Desktop view only - hidden on mobile) */}
              <div className="action-item-desktop-actions align-items-center justify-content-end gap-2 mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-3 d-inline-flex align-items-center gap-1"
                  onClick={handleClose}
                  disabled={saving}
                >
                  <FaTimes />
                  <span>Close</span>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary-gradient px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                  disabled={saving}
                >
                  {editingItem ? (
                    <>
                      <FaSave />
                      {saving ? 'Saving Changes...' : 'Save Changes'}
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      {saving ? 'Adding...' : 'Add Action Item'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </section>
        </div>

        {/* Sticky/Fixed Bottom Action Bar (Mobile only < 768px for thumb-reach accessibility) */}
        <footer className="action-items-bottom-bar" aria-label="Action Items Mobile Bottom Controls">
          <button
            type="button"
            className="btn btn-outline-secondary action-bar-btn action-bar-close-btn d-inline-flex align-items-center justify-content-center gap-1 shadow-sm"
            onClick={handleClose}
            aria-label="Close drawer"
          >
            <FaTimes />
            <span>Close</span>
          </button>

          <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-end">
            <button
              type="button"
              className="btn btn-primary-gradient action-bar-btn action-bar-submit-btn d-inline-flex align-items-center justify-content-center gap-2 shadow-sm fw-semibold w-100"
              onClick={handleSubmit}
              disabled={saving}
            >
              {editingItem ? (
                <>
                  <FaSave />
                  <span>{saving ? 'Saving Changes...' : 'Save Changes'}</span>
                </>
              ) : (
                <>
                  <FaPlus />
                  <span>{saving ? 'Adding...' : 'Add Action Item'}</span>
                </>
              )}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
};

export default ActionItemsDrawer;
